'use client'

import React, { useState, useCallback } from 'react'
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { Resizable } from 're-resizable'
import { BarChart, Activity, CreditCard, Table, X } from 'lucide-react'
import Sidebar from '../Sidebar'
import MainMenu from '../MainMenu'
import Metrics from './widgets/Metrics'
import CompanyCard from './widgets/CompanyCard'
import DataTable from './widgets/DataTable'
import Graph from './widgets/Graph'
import MetricSelector from './widgets/MetricSelector'
import CompanySelector from './CompanySelector'
import './css/MyDashboard.css'

const widgetIcons = {
  metrics: <Activity className="h-4 w-4" />,
  企業カード: <CreditCard className="h-4 w-4" />,
  テーブル: <Table className="h-4 w-4" />,
  グラフ: <BarChart className="h-4 w-4" />,
}

const widgetComponents = {
  metrics: Metrics,
  企業カード: CompanyCard,
  テーブル: DataTable,
  グラフ: Graph,
}

const defaultWidths = {
  metrics: '100%',
  企業カード: '25%',
  テーブル: '100%',
  グラフ: '50%',
}

const MyDashboard = () => {
  const [widgets, setWidgets] = useState([])
  const [widgetOrder, setWidgetOrder] = useState([])
  const [isMetricSidebarOpen, setIsMetricSidebarOpen] = useState(false)
  const [isCompanySidebarOpen, setIsCompanySidebarOpen] = useState(false)
  const [selectedMetricId, setSelectedMetricId] = useState(null)
  const [selectedCompanyCardId, setSelectedCompanyCardId] = useState(null)
  const [metricsData, setMetricsData] = useState({})
  const [companyData, setCompanyData] = useState({})

  const removeWidget = useCallback((widgetId) => {
    setWidgets(prevWidgets => prevWidgets.filter(widget => widget.id !== widgetId))
    setWidgetOrder(prevOrder => prevOrder.filter(id => id !== widgetId))
  }, [])

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return

    if (result.source.droppableId === "sidebar" && result.destination.droppableId === "main") {
      const newWidget = {
        id: `widget-${Date.now()}`,
        type: result.draggableId,
        width: defaultWidths[result.draggableId],
      }
      setWidgets(prevWidgets => {
        const newWidgets = [...prevWidgets]
        newWidgets.splice(result.destination.index, 0, newWidget)
        return newWidgets
      })
      setWidgetOrder(prevOrder => {
        const newOrder = [...prevOrder]
        newOrder.splice(result.destination.index, 0, newWidget.id)
        return newOrder
      })
    } else if (result.source.droppableId === "main" && result.destination.droppableId === "main") {
      setWidgetOrder(prevOrder => {
        const newOrder = Array.from(prevOrder)
        const [reorderedItem] = newOrder.splice(result.source.index, 1)
        newOrder.splice(result.destination.index, 0, reorderedItem)
        return newOrder
      })
    }
  }, [])

  const onResize = useCallback((widgetId, size) => {
    setWidgets(prevWidgets => prevWidgets.map(widget => 
      widget.id === widgetId ? { ...widget, width: `${size.width}px` } : widget
    ))
  }, [])

  const openMetricSidebar = (metricId) => {
    setSelectedMetricId(metricId)
    setIsMetricSidebarOpen(true)
  }

  const openCompanySidebar = (companyCardId) => {
    setSelectedCompanyCardId(companyCardId)
    setIsCompanySidebarOpen(true)
  }

  const closeMetricSidebar = () => {
    setIsMetricSidebarOpen(false)
    setSelectedMetricId(null)
  }

  const closeCompanySidebar = () => {
    setIsCompanySidebarOpen(false)
    setSelectedCompanyCardId(null)
  }

  const onMetricSelect = (category, subCategory) => {
    setMetricsData(prevData => ({
      ...prevData,
      [selectedMetricId]: { category, subCategory }
    }))
    closeMetricSidebar()
  }

  const onCompanySelect = (selectedCompany, selectedData) => {
    setCompanyData(prevData => ({
      ...prevData,
      [selectedCompanyCardId]: { selectedCompany, selectedData }
    }))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsCompanySidebarOpen(false);
      setIsMetricSidebarOpen(false);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <MainMenu />
      <div className="main-container">
        <Sidebar selectedIcon="analysis" />
        <div className="main-contents" onKeyDown={handleKeyDown}>
          <div className="flex h-screen">
            <div className="w-64 bg-gray-100 p-4 overflow-hidden">
              <h2 className="text-lg font-bold mb-4">ウィジェット</h2>
              <Droppable droppableId="sidebar">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {Object.keys(widgetIcons).map((type, index) => (
                      <Draggable key={type} draggableId={type} index={index}>
                        {(provided) => (
                          <div
                            className="w-full mb-2 flex items-center justify-start px-4 py-2 bg-white text-black rounded cursor-move border-gray-300 border"
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            {widgetIcons[type]}
                            <span className="ml-2">{type}</span>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="flex-1 p-4 overflow-x-hidden overflow-y-auto">
              <h1 className="text-2xl font-bold mb-4">タイトル</h1>
              <Droppable droppableId="main">
                {(provided) => (
                  <div className="h-[calc(100vh-8rem)] px-4 overflow-auto/" {...provided.droppableProps} ref={provided.innerRef}>
                    <div className="flex flex-wrap gap-4">
                      {widgetOrder.map((widgetId, index) => {
                        const widget = widgets.find(w => w.id === widgetId)
                        return (
                          <Draggable key={widget.id} draggableId={widget.id} index={index}>
                            {(provided) => (
                              <Resizable
                                size={{ width: widget.width, height: 'auto' }}
                                minWidth={widget.type === '企業カード' || widget.type === 'グラフ' ? '400px' : '100%'}
                                maxWidth="100%"
                                onResizeStop={(e, direction, ref, d) => {
                                  onResize(widget.id, {
                                    width: ref.style.width,
                                    height: ref.style.height,
                                  })
                                }}
                                enable={{ right: true }}
                              >
                                <div
                                  className="bg-white shadow-md rounded-lg relative h-full"
                                  {...provided.draggableProps}
                                  ref={provided.innerRef}
                                >
                                  <div className="p-4 border-b cursor-move" {...provided.dragHandleProps}>
                                    <h3 className="text-lg font-semibold flex items-center">
                                      {widgetIcons[widget.type]}
                                      <span className="ml-2">{widget.type}</span>
                                    </h3>
                                  </div>
                                  <button
                                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200"
                                    onClick={() => removeWidget(widget.id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                  {React.createElement(widgetComponents[widget.type], { 
                                    onOpenMetricSidebar: openMetricSidebar,
                                    onOpenCompanySidebar: openCompanySidebar,
                                    metricsData: metricsData,
                                    companyData: companyData,
                                    widgetId: widget.id
                                  })}
                                </div>
                              </Resizable>
                            )}
                          </Draggable>
                        )
                      })}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </div>
      </div>
      <MetricSelector 
        isOpen={isMetricSidebarOpen} 
        onClose={closeMetricSidebar} 
        onSelect={onMetricSelect} 
      />
      <CompanySelector
        isOpen={isCompanySidebarOpen}
        onClose={closeCompanySidebar}
        onSelect={onCompanySelect}
        initialData={companyData[selectedCompanyCardId]}
      />
    </DragDropContext>
  )
}

export default MyDashboard

